import {DragAndDrop, DragAndDropContext} from '@github-ui/drag-and-drop'
import {noop} from '@github-ui/noop'
import {createContext, useContext, useMemo} from 'react'

import {DropSide} from '../../../helpers/dnd-kit/drop-helpers'
import {resolveRawTitleForGroupingContext} from '../../../helpers/table-group-utilities'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../../hooks/tanstack-table/providers/table-provider'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {useRowDropHandler} from '../../table/row-reordering/hooks/use-row-drop-handler'

export type MoveDialogProps = {
  selectedRow: Row<MemexItemModel>
}

export const MoveDialog = ({close, selectedRow}: MoveDialogProps & {close: () => void}) => {
  const {rows} = useTableInstance()
  const handleDrop = useRowDropHandler()
  const item = selectedRow.original
  const movableRows = getMovableRows(rows, selectedRow)
  const dialogTitle = getDialogTitle(selectedRow)

  return (
    <DragAndDropContext.Provider
      value={useMemo(
        () => ({
          dragIndex: null, // This is not used by the MoveDialog, so it can be null
          overId: null, // This is not used by the MoveDialog, so it can be null
          direction: 'vertical', // This is not used by the MoveDialog, so it can be `vertical`
          isInDragMode: false, // This is not used by the MoveDialog, so it can be false
          moveDialogItem: {
            title: item.getRawTitle(),
            index: movableRows.findIndex(row => row.original.id === item.id),
          },
          openMoveDialog: noop, // This is not used by the MoveDialog, so it can be a noop
          moveToPosition: (async (currentPosition: number, newPosition: number, isBefore?: boolean) => {
            const activeItem = movableRows[currentPosition]?.original
            const overItem = movableRows[newPosition]?.original
            if (!activeItem || !overItem) {
              return
            }
            // Close the dialog before the `handleDrop` mutation finishes execution to prevent users from perceiving the operation as slow
            close()
            await handleDrop({
              activeItem,
              overItem,
              side: isBefore ? DropSide.BEFORE : DropSide.AFTER,
            })
          }) as any,
          items: movableRows.map(row => ({
            title: row.original.getRawTitle(),
            id: row.original.id,
          })),
        }),
        [close, movableRows, item, handleDrop],
      )}
    >
      <DragAndDrop.DragAndDropMoveDialog
        dialogTitle={dialogTitle}
        closeDialog={() => {
          close()
        }}
      />
    </DragAndDropContext.Provider>
  )
}

export function getMovableRows(
  rows: Array<Row<MemexItemModel>>,
  selectedRow: Row<MemexItemModel>,
): Array<Row<MemexItemModel>> {
  // Parent is a grouped row or a parent issue or undefined
  const parentRow = selectedRow.getParentRow()
  // If the parent is a grouped row, limit to sibling rows
  if (parentRow && parentRow.getIsGrouped()) {
    return parentRow.subRows ?? []
  } else if (parentRow) {
    // Cannot move nested subissues (yet)
    return []
  }
  // For ungrouped views, all rows are movable
  return rows
}

function getDialogTitle(selectedRow: Row<MemexItemModel>): string | undefined {
  const parentRow = selectedRow.getParentRow()
  if (!parentRow || !parentRow.getIsGrouped()) return
  // eslint-disable-next-line i18n-text/no-en
  return `Move selected item within ${resolveRawTitleForGroupingContext(parentRow.groupedSourceObject)}`
}

export type MoveDialogContextProps = {
  setMoveDialogProps: (props: MoveDialogProps | null) => void
}

export const MoveDialogContext = createContext<MoveDialogContextProps>({
  setMoveDialogProps: () => {},
})

export const useMoveDialog = () => {
  const {setMoveDialogProps} = useContext(MoveDialogContext)

  return {setMoveDialogProps}
}

try{ MoveDialog.displayName ||= 'MoveDialog' } catch {}
try{ MoveDialogContext.displayName ||= 'MoveDialogContext' } catch {}